"use client";

import { useEffect, useRef, useState } from "react";
import type { NotificationType } from "@/lib/db/schema";
import { cn } from "@/lib/utils";

interface NotificationContentProps {
    title: string;
    body: string;
    isRead: boolean;
    timeLabel: string;
}

interface NotificationMenuContentProps extends NotificationContentProps {
    bodyTestId?: string;
}

function NotificationTitle({
    title,
    isRead,
    shouldTruncate = false,
}: {
    title: string;
    isRead: boolean;
    shouldTruncate?: boolean;
}) {
    return (
        <span className="flex items-center gap-2">
            <span
                className={cn(
                    "text-sm font-semibold",
                    shouldTruncate && "min-w-0 truncate",
                )}
            >
                {title}
            </span>
            {!isRead && (
                <>
                    <span
                        className="size-2 shrink-0 rounded-full bg-accent"
                        aria-hidden
                    />
                    <span className="sr-only">(unread)</span>
                </>
            )}
        </span>
    );
}

export function NotificationMenuContent({
    title,
    body,
    isRead,
    timeLabel,
    bodyTestId,
}: NotificationMenuContentProps) {
    return (
        <span className="min-w-0 flex-1">
            <NotificationTitle title={title} isRead={isRead} shouldTruncate />
            <span
                data-testid={bodyTestId}
                className="mt-0.5 block whitespace-normal text-sm text-muted-foreground line-clamp-2"
            >
                {body}
            </span>
            <span className="mt-1 block text-xs text-muted-foreground/70">
                {timeLabel}
            </span>
        </span>
    );
}

interface NotificationFeedContentProps extends NotificationContentProps {
    bodyId: string;
    bodyTestId?: string;
    onExpand?: () => void;
    onSelect?: () => void;
    type: NotificationType;
}

export function NotificationFeedContent({
    title,
    body,
    isRead,
    timeLabel,
    bodyId,
    bodyTestId,
    onExpand,
    onSelect,
    type,
}: NotificationFeedContentProps) {
    const bodyRef = useRef<HTMLSpanElement>(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [hasOverflow, setHasOverflow] = useState(false);
    const isCollapsible = type === "admin_message";

    useEffect(() => {
        if (!isCollapsible || isExpanded) return;
        const element = bodyRef.current;
        if (!element) return;

        const measure = () => {
            setHasOverflow(element.scrollHeight > element.clientHeight + 1);
        };
        const frame = window.requestAnimationFrame(measure);
        const observer =
            typeof ResizeObserver === "undefined"
                ? null
                : new ResizeObserver(measure);
        observer?.observe(element);
        window.addEventListener("resize", measure);

        return () => {
            window.cancelAnimationFrame(frame);
            observer?.disconnect();
            window.removeEventListener("resize", measure);
        };
    }, [body, isCollapsible, isExpanded]);

    const content = (
        <>
            <NotificationTitle title={title} isRead={isRead} />
            <span
                ref={bodyRef}
                id={bodyId}
                data-testid={bodyTestId}
                className={cn(
                    "mt-1 block whitespace-pre-line text-sm leading-6 text-muted-foreground",
                    isCollapsible && !isExpanded && "line-clamp-4",
                )}
            >
                {body}
            </span>
        </>
    );

    return (
        <div className="min-w-0 flex-1">
            {onSelect ? (
                <button
                    type="button"
                    onClick={onSelect}
                    className="block w-full text-left cursor-pointer"
                >
                    {content}
                </button>
            ) : (
                <div>{content}</div>
            )}
            {isCollapsible && hasOverflow && (
                <button
                    type="button"
                    aria-controls={bodyId}
                    aria-expanded={isExpanded}
                    onClick={() => {
                        const willExpand = !isExpanded;
                        setIsExpanded(willExpand);
                        if (willExpand) onExpand?.();
                    }}
                    className="mt-2 block w-fit text-sm font-semibold text-primary hover:underline"
                >
                    {isExpanded ? "Show less" : "Show full message"}
                </button>
            )}
            <span className="mt-1 block text-xs text-muted-foreground/70">
                {timeLabel}
            </span>
        </div>
    );
}
